import { Button, CircularProgress } from '@atgfr8/atg-ui-tailwind';
import { BasicModal } from '@components/Modal/BasicModal';
import { RadioGroup } from '@components/RadioGroup/RadioGroup';
import { TextArea } from '@components/TextField/TextArea';
import { ToastType } from '@components/Toast/Toast';
import { closeModal, openModal } from '@store/modals/store';
import { setActiveCarrierTab } from '@store/orders/actions/carrierActions';
import {
  carrierStore,
  orderStore,
  setLoadSureStore,
} from '@store/orders/store';
import { BounceReasonCode, TabType } from '@store/orders/types';
import { handleToast } from '@utils/utils';
import { BOUNCE_CARRIER_MODAL_ID } from '@views/order/constants';
import { createSignal } from 'solid-js';

import {
  cancelInsuranceCbModal,
  isLoadSureVendorAttached,
} from './LoadSureHelpers';
import { bounceCarrierFromLoad } from './utils';

const BOUNCE_REASON_CODES: BounceReasonCode[] = [
  { id: 1, name: 'Carrier' },
  { id: 2, name: 'Customer' },
  { id: 3, name: 'Armstrong' },
  { id: 4, name: 'Weather' },
  { id: 5, name: 'Bounce History on Shipment' },
];

type PropsT = {
  tabIndex: number;
};

export const BounceCarrierModal = (props: PropsT) => {
  const [reasonCodeId, setReasonCodeId] = createSignal<number>();
  const [notes, setNotes] = createSignal('');
  const [loading, setLoading] = createSignal(false);

  const load = () => orderStore.order.loads[props.tabIndex];
  const carrierName = () =>
    carrierStore.carrier[props.tabIndex]?.name || (load().carrierName ?? '');

  const reset = () => {
    setReasonCodeId(undefined);
    setNotes('');
  };

  const close = () => {
    reset();
    closeModal(BOUNCE_CARRIER_MODAL_ID);
  };

  const submit = async () => {
    const selectedReasonCodeId = reasonCodeId();
    const tabIndex = props.tabIndex;
    const currentLoad = load();
    const loadId = currentLoad.id;
    const loadNum = currentLoad.loadNum;
    const bouncedCarrierId = currentLoad.carrierId;
    const bouncedCarrier = carrierName();
    if (
      selectedReasonCodeId === undefined ||
      !(loadId > 0) ||
      !bouncedCarrierId
    )
      return;
    const payload = {
      loadId,
      reasonId: selectedReasonCodeId,
      notes: notes().trim() || null,
    };

    const bounce = async () => {
      setLoading(true);
      try {
        await bounceCarrierFromLoad(payload, tabIndex, bouncedCarrierId);
        close();
        handleToast(
          ToastType.Success,
          `Bounced ${bouncedCarrier} from Load #${loadNum}`,
        );
      } catch (error) {
        handleToast(ToastType.Error, (error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    if (isLoadSureVendorAttached()) {
      setLoadSureStore('successCallback', () => () => {
        setActiveCarrierTab({ index: tabIndex, type: TabType.load });
        void bounce();
      });
      close();
      openModal(cancelInsuranceCbModal);
      return;
    }

    await bounce();
  };

  return (
    <BasicModal
      id={BOUNCE_CARRIER_MODAL_ID}
      title="Bounce Carrier"
      width="560px"
      footer={false}
      onClose={reset}
    >
      <div class="flex flex-col gap-3 text-armstrong-primary">
        <div class="text-sm">
          Removing <span class="font-bold">{carrierName()}</span> from Load{' '}
          {load().loadNum}. Select a reason code - the bounce is saved to the
          shipment history and the carrier profile.
        </div>
        <RadioGroup<number>
          label="Reason Code"
          labelClass="text-sm font-semibold"
          direction="vertical"
          optionClass="self-start"
          color="info"
          required
          value={reasonCodeId()}
          onChange={setReasonCodeId}
          options={BOUNCE_REASON_CODES.map((code) => ({
            label: code.name,
            value: code.id,
          }))}
        />
        <TextArea
          label="Notes (optional)"
          value={notes()}
          onInput={(e) => setNotes(e.target.value)}
          maxChar={2000}
          rows={3}
        />
        <div class="flex justify-end gap-2">
          <Button
            variant="outlined"
            color="info"
            onClick={close}
            disabled={loading()}
            dataTestId="bounce-carrier-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void submit()}
            disabled={
              loading() || orderStore.isSaving || reasonCodeId() === undefined
            }
            endIcon={loading() ? <CircularProgress size={20} /> : null}
            dataTestId="bounce-carrier-confirm-button"
          >
            Bounce Carrier
          </Button>
        </div>
      </div>
    </BasicModal>
  );
};
